import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, LogOut, LayoutDashboard, Menu, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { InstallPWA } from '@/components/InstallPWA';
import { useState } from 'react';

export const Navigation = () => {
  const { user, logout } = useAuth();
  const { getItemCount } = useCart();
  const { setHomepageTheme, THEMES } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const switchToPremium = () => {
    setHomepageTheme(THEMES.PREMIUM);
    navigate('/home-premium');
  };

  const categories = [
    { name: 'WORKSTATIONS', path: '/products?category=Modular Office Workstation' },
    { name: 'STORAGE', path: '/products?category=Storage Cabinets' },
    { name: 'PRIVACY BOOTHS', path: '/products?category=Privacy Booths' },
    { name: 'DELIVERED WORKSPACES', path: '/projects' },
    { name: 'ABOUT', path: '/about' },
    { name: 'CONTACT', path: '/contact' },
    { name: 'BLOG', path: '/blog' }
  ];

  return (
    <>
      {/* Made in India Top Bar - Sticky */}
      <div className="sticky top-0 z-[60] bg-primary text-center py-1.5 overflow-hidden">
        <div className="bg-gradient-to-r from-[#FF9933] via-white to-[#138808]">
          <div className="flex items-center justify-center gap-2 py-0.5">
            <img 
              src="https://flagcdn.com/w40/in.png" 
              alt="India Flag"
              className="w-5 h-3.5 object-cover rounded-sm shadow-sm"
            />
            <p className="text-[10px] sm:text-xs font-bold text-gray-800 tracking-wide">
              Trusted by 5000+ Offices Since 2014 | A Division of Ezeefitofficesystems.in | Factory Direct
            </p>
            <img 
              src="https://flagcdn.com/w40/in.png" 
              alt="India Flag"
              className="w-5 h-3.5 object-cover rounded-sm shadow-sm"
            />
          </div>
        </div>
      </div>
      
      <nav data-testid="main-navigation" className="sticky top-[30px] z-50 bg-background/80 backdrop-blur-md border-b border-border relative">
      <div className="container mx-auto px-4 sm:px-8 py-2 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo with Heritage Badge */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_f9ac7b10-4770-40cb-a871-c30b54e04789/artifacts/jt1touec_LOGO-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer.png" 
              alt="Modular Furniture Store" 
              className="h-16 sm:h-24 md:h-32 lg:h-40 w-auto transition-all hover:scale-105"
              style={{ mixBlendMode: 'multiply' }}
            />
            <span className="hidden md:block text-[10px] md:text-xs text-muted-foreground font-medium tracking-wide italic border-l-2 border-primary/30 pl-3">
              By Ezeefit –<br />10 Years of Workspace Excellence
            </span>
          </Link>

          {/* Right Side Container - Category Links + Actions */}
          <div className="flex items-center space-x-8">
            {/* Category Links */}
            <div className="hidden md:flex items-center space-x-8">
              {categories.map((category, index) => (
                <Link
                  key={index}
                  to={category.path}
                  data-testid={`nav-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="uppercase tracking-widest text-xs font-bold hover:text-accent transition-colors"
                >
                  {category.name}
                </Link>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center space-x-4">
            {/* Install PWA Button */}
            <InstallPWA />
            
            <Link to="/cart" data-testid="cart-button" className="relative hover:text-accent transition-colors">
              <ShoppingCart className="w-6 h-6" />
              {getItemCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-accent text-accent-foreground text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {getItemCount()}
                </span>
              )}
            </Link>

            {user ? (
              <>
                {user.is_admin && (
                  <Link to="/admin/dashboard" data-testid="admin-dashboard-link" className="hidden md:block">
                    <Button variant="ghost" size="sm" className="rounded-none uppercase tracking-wider">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      ADMIN
                    </Button>
                  </Link>
                )}
                <Link to="/profile" data-testid="profile-link" className="hidden md:block">
                  <Button variant="ghost" size="sm" className="rounded-none uppercase tracking-wider">
                    <User className="w-4 h-4 mr-2" />
                    {user.name}
                  </Button>
                </Link>
                <Button 
                  onClick={handleLogout} 
                  data-testid="logout-button"
                  variant="ghost" 
                  size="sm" 
                  className="hidden md:flex rounded-none uppercase tracking-wider"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  LOGOUT
                </Button>
              </>
            ) : (
              <Link to="/login" data-testid="login-link">
                <Button className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground rounded-none uppercase tracking-wider font-bold px-4 md:px-8 py-2 md:py-4 text-xs md:text-sm">
                  LOGIN
                </Button>
              </Link>
            )}
            
            {/* Mobile Hamburger Menu */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-muted rounded transition-colors"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed top-[72px] sm:top-[88px] left-0 right-0 bg-background border-b border-border shadow-lg z-[100]">
          <nav className="container mx-auto px-4 py-4 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-1">
              {categories.map((category, index) => (
                <Link 
                  key={index}
                  to={category.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm tracking-[0.1em] font-semibold text-foreground hover:text-primary hover:bg-muted/50 transition-colors py-3 px-4 border-b border-border/50"
                >
                  {category.name}
                </Link>
              ))}
              
              {/* Mobile User Actions */}
              {user ? (
                <div className="border-t border-border pt-3 mt-2">
                  {user.is_admin && (
                    <Link 
                      to="/admin/dashboard" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 text-sm tracking-[0.1em] font-semibold text-foreground hover:text-primary py-3 px-4"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      ADMIN DASHBOARD
                    </Link>
                  )}
                  <Link 
                    to="/profile" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 text-sm tracking-[0.1em] font-semibold text-foreground hover:text-primary py-3 px-4"
                  >
                    <User className="w-4 h-4" />
                    MY PROFILE
                  </Link>
                  <button 
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-2 text-sm tracking-[0.1em] font-semibold text-red-600 hover:text-red-700 py-3 px-4 w-full text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    LOGOUT
                  </button>
                </div>
              ) : null}
            </div>
          </nav>
        </div>
      )}
    </nav>
    </>
  );
};
